import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Users as UsersIcon,
  CheckCircle,
  Lock,
  Filter,
  Eye,
  ShieldOff,
  ShieldCheck,
  Building2,
  Bike,
  ShoppingBag,
  Briefcase,
  X,
  UserPlus,
  LogOut,
  Smartphone,
  Pencil,
  Trash2,
} from 'lucide-react';
import { cn } from '@lib/utils';
import { isValidKenyanPhone, isValidEmail, normalizeKenyanPhone } from '@lib/validators';
import { usersService } from '@services/users.service';
import { opsService } from '@services/ops.service';
import type { UserSummaryResponse } from '@apptypes/index';
import { UserDetailModal } from '@components/users/UserDetailModal';
import { PageGuard } from '@components/PageGuard';
import { PermissionGate } from '@components/PermissionGate';
import { UserEditModal } from '@components/users/UserEditModal';
import { toast } from 'sonner';

const statusColors: Record<string, string> = {
  active: 'bg-[var(--state-success-bg)] text-[var(--state-success)]',
  suspended: 'bg-[var(--state-danger-bg)] text-[var(--state-danger)]',
  deactivated: 'bg-[var(--bg-base)] text-[var(--text-muted)]',
  pending_verification: 'bg-[var(--state-warning-bg)] text-[var(--state-warning)]',
};

const statusDots: Record<string, string> = {
  active: 'bg-[var(--state-success)]',
  suspended: 'bg-[var(--state-danger)]',
  deactivated: 'bg-[var(--text-muted)]',
  pending_verification: 'bg-[var(--state-warning)]',
};

// ── Create User Modal ─────────────────────────────────────────────────────────

function CreateUserModal({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();

  const [phone, setPhone] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [cheKitStaff, setCheKitStaff] = useState(false);
  const [customer, setCustomer] = useState(false);

  const [formError, setFormError] = useState<string | null>(null);

  const createMutation = useMutation({
    mutationFn: () =>
      usersService.create({
        phone: normalizeKenyanPhone(phone) ?? phone,
        firstName: firstName || undefined,
        lastName: lastName || undefined,
        email: email || undefined,
        password: password || undefined,
        cheKitStaff,
        customer,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      onClose();
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValidKenyanPhone(phone)) {
      setFormError('Enter a valid Kenyan number, e.g. 0712 345 678.');
      return;
    }
    if (email.trim() && !isValidEmail(email)) {
      setFormError('Enter a valid email address.');
      return;
    }
    setFormError(null);
    createMutation.mutate();
  };

  const inputClass =
    'w-full rounded-2xl border border-[var(--border-default)] bg-white px-4 py-3 text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--border-strong)] transition-colors duration-200';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[var(--brand-forest)]/50 backdrop-blur-sm">
      <div className="bg-white rounded-3xl shadow-lift w-full max-w-md">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-[var(--border-subtle)]">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)]">
              Users
            </p>
            <h2 className="font-display text-[22px] font-medium tracking-[-0.01em] text-[var(--text-primary)] mt-0.5">
              Create User
            </h2>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-full text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-base)] transition-all duration-200"
          >
            <X className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit}>
          <div className="p-6 space-y-5">
            <div>
              <label className="block text-[12px] font-semibold uppercase tracking-[0.08em] text-[var(--text-primary)] mb-2">
                Phone <span className="text-[var(--state-danger)]">*</span>
              </label>
              <input
                type="text"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+254712345678"
                required
                className={inputClass}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-[12px] font-semibold uppercase tracking-[0.08em] text-[var(--text-primary)] mb-2">
                  First Name
                </label>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="Jane"
                  className={inputClass}
                />
              </div>
              <div>
                <label className="block text-[12px] font-semibold uppercase tracking-[0.08em] text-[var(--text-primary)] mb-2">
                  Last Name
                </label>
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Doe"
                  className={inputClass}
                />
              </div>
            </div>

            <div>
              <label className="block text-[12px] font-semibold uppercase tracking-[0.08em] text-[var(--text-primary)] mb-2">
                Email{' '}
                <span className="text-[var(--text-muted)] font-normal normal-case tracking-normal">
                  (optional)
                </span>
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="jane@example.com"
                className={inputClass}
              />
            </div>

            <div>
              <label className="block text-[12px] font-semibold uppercase tracking-[0.08em] text-[var(--text-primary)] mb-2">
                Password{' '}
                <span className="text-[var(--text-muted)] font-normal normal-case tracking-normal">
                  (optional)
                </span>
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Leave blank to generate"
                className={inputClass}
              />
            </div>

            <div className="space-y-3 pt-1">
              <p className="text-[12px] font-semibold uppercase tracking-[0.08em] text-[var(--text-muted)]">
                User Flags
              </p>
              <label className="flex items-center gap-3 cursor-pointer select-none group">
                <input
                  type="checkbox"
                  checked={cheKitStaff}
                  onChange={(e) => setCheKitStaff(e.target.checked)}
                  className="w-4 h-4 rounded border-[var(--border-default)] accent-[var(--brand-leaf)]"
                />
                <span className="text-[14px] text-[var(--text-secondary)] group-hover:text-[var(--text-primary)] transition-colors">
                  Chekit Staff
                </span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer select-none group">
                <input
                  type="checkbox"
                  checked={customer}
                  onChange={(e) => setCustomer(e.target.checked)}
                  className="w-4 h-4 rounded border-[var(--border-default)] accent-[var(--brand-leaf)]"
                />
                <span className="text-[14px] text-[var(--text-secondary)] group-hover:text-[var(--text-primary)] transition-colors">
                  Customer
                </span>
              </label>
            </div>

            {createMutation.isError && (
              <div className="px-4 py-3 bg-[var(--state-danger-bg)] border border-[var(--state-danger-border)] rounded-2xl text-[13px] text-[var(--state-danger)]">
                {(createMutation.error as Error)?.message ?? 'Failed to create user. Please try again.'}
              </div>
            )}
          </div>

          {/* Footer */}
          {formError && (
            <p className="px-6 pt-4 text-[13px] font-medium text-[var(--state-danger)]">
              {formError}
            </p>
          )}
          <div className="flex items-center justify-end gap-3 px-6 py-5 border-t border-[var(--border-subtle)]">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center rounded-full border border-[var(--border-subtle)] px-5 py-2.5 text-[14px] font-semibold text-[var(--text-primary)] hover:border-[var(--border-default)] hover:bg-[var(--bg-base)] transition-all duration-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending || !phone.trim()}
              className="inline-flex items-center gap-2 rounded-full bg-[var(--brand-forest)] px-5 py-2.5 text-[14px] font-semibold text-white transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed hover:brightness-110 hover:shadow-[0_12px_32px_rgba(124,179,66,0.45)] active:brightness-95"
            >
              <UserPlus className="w-4 h-4" strokeWidth={1.5} />
              {createMutation.isPending ? 'Creating…' : 'Create User'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export const UsersManagementPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedUser, setSelectedUser] = useState<UserSummaryResponse | null>(null);
  const [devicesUser, setDevicesUser] = useState<UserSummaryResponse | null>(null);
  const [editUser, setEditUser] = useState<UserSummaryResponse | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const itemsPerPage = 10;

  const queryClient = useQueryClient();

  const { data: users = [], isLoading, error } = useQuery({
    queryKey: ['admin-users'],
    queryFn: () => usersService.list({ size: 200 }),
  });

  const suspendMutation = useMutation({
    mutationFn: ({ userId, reason }: { userId: string; reason?: string }) =>
      usersService.suspend(userId, reason),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-users'] }),
  });

  const forceLogoutMutation = useMutation({
    mutationFn: (userId: string) => opsService.forceLogout(userId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-users'] }),
  });

  const activateMutation = useMutation({
    mutationFn: (userId: string) => usersService.activate(userId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-users'] }),
  });

  const deleteMutation = useMutation({
    mutationFn: (userId: string) => usersService.remove(userId),
    onSuccess: () => { toast.success('User deleted and anonymised.'); queryClient.invalidateQueries({ queryKey: ['admin-users'] }); },
    onError: (e) => toast.error((e as { message?: string })?.message ?? 'Could not delete this user.'),
  });

  // Derived stats
  const totalUsers = users.length;
  const activeUsers = users.filter((u) => u.status === 'active').length;
  const suspendedUsers = users.filter((u) => u.status === 'suspended').length;
  const staffUsers = users.filter((u) => u.cheKitStaff).length;

  // Filter
  const filteredUsers = users.filter((u) => {
    const q = searchQuery.toLowerCase();
    const matchesSearch =
      !q ||
      (u.firstName ?? '').toLowerCase().includes(q) ||
      (u.lastName ?? '').toLowerCase().includes(q) ||
      (u.phone ?? '').includes(q) ||
      (u.email ?? '').toLowerCase().includes(q);

    const matchesStatus = statusFilter === 'all' || u.status === statusFilter;

    const matchesType =
      typeFilter === 'all' ||
      (typeFilter === 'staff' && u.cheKitStaff) ||
      (typeFilter === 'business' && u.businessUser) ||
      (typeFilter === 'customer' && u.customer) ||
      (typeFilter === 'rider' && u.rider);

    return matchesSearch && matchesStatus && matchesType;
  });

  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const start = (currentPage - 1) * itemsPerPage;
  const paged = filteredUsers.slice(start, start + itemsPerPage);

  const getInitials = (u: UserSummaryResponse) =>
    `${u.firstName[0] ?? ''}${u.lastName[0] ?? ''}`.toUpperCase();

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString('en-KE', { day: '2-digit', month: 'short', year: 'numeric' });

  const selectClass =
    'w-full rounded-2xl border border-[var(--border-default)] bg-white px-4 py-2.5 text-[14px] text-[var(--text-primary)] focus:border-[var(--border-strong)] transition-colors duration-200';

  return (
    <PageGuard permission="users.read">
    <div>
      {/* Page header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)]">
            Platform
          </p>
          <h1 className="font-display text-[32px] font-medium tracking-[-0.02em] text-[var(--text-primary)] mt-1">
            Users
          </h1>
          <p className="text-[14px] text-[var(--text-muted)] mt-1">
            Manage platform users, staff flags, and access
          </p>
        </div>
        <PermissionGate permission="users.create">
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center gap-2 rounded-full bg-[var(--brand-forest)] px-5 py-3 text-[14px] font-semibold text-white transition-all duration-300 hover:brightness-110 hover:shadow-[0_12px_32px_rgba(124,179,66,0.45)] active:brightness-95"
          >
            <UserPlus className="w-4 h-4" strokeWidth={1.5} />
            Create User
          </button>
        </PermissionGate>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {[
          // 'Total' is a count, not a state, so it takes the brand accent rather
          // than a semantic colour. #0891b2 here was a banned cyan.
          { label: 'Total Users', value: totalUsers, icon: UsersIcon, accent: 'var(--text-accent)' },
          { label: 'Active', value: activeUsers, icon: CheckCircle, accent: 'var(--state-success)' },
          { label: 'Suspended', value: suspendedUsers, icon: Lock, accent: 'var(--state-danger)' },
          { label: 'Chekit Staff', value: staffUsers, icon: Briefcase, accent: 'var(--brand-leaf)' },
        ].map(({ label, value, icon: Icon, accent }) => (
          <div key={label} className="rounded-3xl bg-white shadow-soft p-6">
            <div className="w-10 h-10 rounded-2xl bg-[var(--bg-base)] flex items-center justify-center mb-4">
              <Icon className="w-5 h-5" style={{ color: accent }} strokeWidth={1.5} />
            </div>
            <p className="text-[12px] font-semibold uppercase tracking-[0.08em] text-[var(--text-muted)]">
              {label}
            </p>
            <p className="font-display text-[36px] font-medium tracking-[-0.03em] text-[var(--text-primary)] mt-1">
              {isLoading ? '—' : value}
            </p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="rounded-3xl bg-white shadow-soft p-6 mb-6">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-[12px] font-semibold uppercase tracking-[0.08em] text-[var(--text-muted)] mb-2">
              Search
            </label>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
              placeholder="Name, phone, or email…"
              className="w-full rounded-full border border-[var(--border-subtle)] bg-[var(--bg-base)] px-4 py-2.5 text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--border-default)] transition-colors duration-200"
            />
          </div>
          <div className="w-44">
            <label className="block text-[12px] font-semibold uppercase tracking-[0.08em] text-[var(--text-muted)] mb-2">
              Status
            </label>
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
              className={selectClass}
            >
              <option value="all">All Statuses</option>
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
              <option value="deactivated">Deactivated</option>
              <option value="pending_verification">Pending Verification</option>
            </select>
          </div>
          <div className="w-44">
            <label className="block text-[12px] font-semibold uppercase tracking-[0.08em] text-[var(--text-muted)] mb-2">
              Type
            </label>
            <select
              value={typeFilter}
              onChange={(e) => { setTypeFilter(e.target.value); setCurrentPage(1); }}
              className={selectClass}
            >
              <option value="all">All Types</option>
              <option value="staff">Chekit Staff</option>
              <option value="business">Business User</option>
              <option value="customer">Customer</option>
              <option value="rider">Rider</option>
            </select>
          </div>
          <button
            onClick={() => { setSearchQuery(''); setStatusFilter('all'); setTypeFilter('all'); setCurrentPage(1); }}
            className="inline-flex items-center gap-2 rounded-full border border-[var(--border-subtle)] bg-white px-5 py-2.5 text-[14px] font-semibold text-[var(--text-primary)] hover:border-[var(--border-default)] hover:bg-[var(--bg-base)] transition-all duration-200"
          >
            <Filter className="w-4 h-4" strokeWidth={1.5} />
            Clear
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-3xl bg-white shadow-soft overflow-hidden">
        <div className="px-6 py-5 border-b border-[var(--border-subtle)]">
          <h2 className="text-[15px] font-semibold text-[var(--text-primary)]">
            Users{' '}
            <span className="text-[var(--text-muted)] font-normal">
              ({filteredUsers.length}{users.length > filteredUsers.length ? ` of ${users.length}` : ''})
            </span>
          </h2>
        </div>

        {isLoading && (
          <div className="p-12 text-center text-[14px] text-[var(--text-muted)]">Loading users…</div>
        )}
        {error && (
          <div className="p-12 text-center text-[14px] text-[var(--state-danger)]">Failed to load users.</div>
        )}

        {!isLoading && !error && (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[var(--border-subtle)] bg-[var(--bg-base)]">
                    {['User', 'Type Flags', 'Status', 'Joined', 'Actions'].map((h) => (
                      <th
                        key={h}
                        className="px-6 py-4 text-left text-[12px] font-semibold uppercase tracking-[0.08em] text-[var(--text-muted)]"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--border-subtle)]">
                  {paged.map((user) => (
                    <tr key={user.userId} className="hover:bg-[var(--bg-base)] transition-colors duration-150">
                      {/* User info */}
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-2xl flex items-center justify-center bg-[var(--bg-elevated)] text-[var(--text-accent)] font-semibold text-[13px] shrink-0">
                            {getInitials(user)}
                          </div>
                          <div>
                            <div className="text-[14px] font-semibold text-[var(--text-primary)]">
                              {user.firstName} {user.lastName}
                            </div>
                            <div className="text-[12px] text-[var(--text-muted)]">{user.phone}</div>
                            {user.email && (
                              <div className="text-[12px] text-[var(--text-muted)]">{user.email}</div>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* Type flags */}
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-1.5">
                          {user.cheKitStaff && (
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-[var(--bg-elevated)] text-[var(--text-accent)] text-[12px] font-semibold rounded-full">
                              <Briefcase className="w-3 h-3" strokeWidth={1.5} /> Staff
                            </span>
                          )}
                          {user.businessUser && (
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-[var(--bg-elevated)] text-[var(--text-accent)] text-[12px] font-semibold rounded-full">
                              <Building2 className="w-3 h-3" strokeWidth={1.5} /> Business
                            </span>
                          )}
                          {user.customer && (
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-[var(--state-success-bg)] text-[var(--state-success)] text-[12px] font-semibold rounded-full">
                              <ShoppingBag className="w-3 h-3" strokeWidth={1.5} /> Customer
                            </span>
                          )}
                          {user.rider && (
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-[var(--state-warning-bg)] text-[var(--state-warning)] text-[12px] font-semibold rounded-full">
                              <Bike className="w-3 h-3" strokeWidth={1.5} /> Rider
                            </span>
                          )}
                          {!user.cheKitStaff && !user.businessUser && !user.customer && !user.rider && (
                            <span className="text-[13px] text-[var(--text-muted)]">—</span>
                          )}
                        </div>
                      </td>

                      {/* Status */}
                      <td className="px-6 py-4">
                        <span className={cn(
                          'inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[12px] font-semibold',
                          statusColors[user.status] ?? 'bg-[var(--bg-base)] text-[var(--text-muted)]'
                        )}>
                          <span className={cn(
                            'w-1.5 h-1.5 rounded-full',
                            statusDots[user.status] ?? 'bg-[var(--text-muted)]'
                          )} />
                          {user.status.replace('_', ' ')}
                        </span>
                      </td>

                      {/* Joined */}
                      <td className="px-6 py-4 text-[13px] text-[var(--text-muted)]">
                        {formatDate(user.createdAt)}
                      </td>

                      {/* Actions */}
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-1.5">
                          <button
                            onClick={() => setSelectedUser(user)}
                            className="w-8 h-8 flex items-center justify-center rounded-xl border border-[var(--border-subtle)] text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:border-[var(--border-default)] hover:bg-[var(--bg-base)] transition-all duration-200"
                            title="View"
                          >
                            <Eye className="w-4 h-4" strokeWidth={1.5} />
                          </button>
                          {user.status === 'active' && (
                            <PermissionGate permission="users.suspend">
                              <button
                                onClick={() => suspendMutation.mutate({ userId: user.userId })}
                                disabled={suspendMutation.isPending}
                                className="w-8 h-8 flex items-center justify-center rounded-xl border border-[var(--border-subtle)] text-[var(--text-muted)] hover:text-[var(--state-danger)] hover:border-[var(--state-danger-border)] hover:bg-[var(--state-danger-bg)] transition-all duration-200 disabled:opacity-40"
                                title="Suspend"
                              >
                                <ShieldOff className="w-4 h-4" strokeWidth={1.5} />
                              </button>
                            </PermissionGate>
                          )}
                          {user.status === 'suspended' && (
                            <PermissionGate permission="users.activate">
                              <button
                                onClick={() => activateMutation.mutate(user.userId)}
                                disabled={activateMutation.isPending}
                                className="w-8 h-8 flex items-center justify-center rounded-xl border border-[var(--border-subtle)] text-[var(--text-muted)] hover:text-[var(--state-success)] hover:border-[var(--state-success-border)] hover:bg-[var(--state-success-bg)] transition-all duration-200 disabled:opacity-40"
                                title="Activate"
                              >
                                <ShieldCheck className="w-4 h-4" strokeWidth={1.5} />
                              </button>
                            </PermissionGate>
                          )}
                          <button
                            onClick={() => setDevicesUser(user)}
                            className="w-8 h-8 flex items-center justify-center rounded-xl border border-[var(--border-subtle)] text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:border-[var(--border-default)] hover:bg-[var(--bg-base)] transition-all duration-200"
                            title="Registered devices & app versions"
                          >
                            <Smartphone className="w-4 h-4" strokeWidth={1.5} />
                          </button>
                          <PermissionGate permission="users.suspend">
                            <button
                              onClick={() => {
                                if (window.confirm(`Force logout ${user.firstName ?? ''} ${user.lastName ?? ''}? All sessions are revoked and their apps sign out immediately.`)) {
                                  forceLogoutMutation.mutate(user.userId);
                                }
                              }}
                              disabled={forceLogoutMutation.isPending}
                              className="w-8 h-8 flex items-center justify-center rounded-xl border border-[var(--border-subtle)] text-[var(--text-muted)] hover:text-[var(--state-warning)] hover:border-[var(--state-warning-border)] hover:bg-[var(--state-warning-bg)] transition-all duration-200 disabled:opacity-40"
                              title="Force logout everywhere"
                            >
                              <LogOut className="w-4 h-4" strokeWidth={1.5} />
                            </button>
                          </PermissionGate>
                          {user.status !== 'deleted' && (
                            <PermissionGate permission="users.update">
                              <button
                                onClick={() => setEditUser(user)}
                                className="w-8 h-8 flex items-center justify-center rounded-xl border border-[var(--border-subtle)] text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:border-[var(--border-default)] hover:bg-[var(--bg-base)] transition-all duration-200"
                                title="Edit"
                              >
                                <Pencil className="w-4 h-4" strokeWidth={1.5} />
                              </button>
                            </PermissionGate>
                          )}
                          {user.status !== 'deleted' && (
                            <PermissionGate permission="users.delete">
                              <button
                                onClick={() => {
                                  if (window.confirm(`Permanently delete ${user.firstName ?? ''} ${user.lastName ?? ''}? Their personal details are erased and they can no longer sign in. Orders, wallets and history are kept. This cannot be undone.`)) {
                                    deleteMutation.mutate(user.userId);
                                  }
                                }}
                                disabled={deleteMutation.isPending}
                                className="w-8 h-8 flex items-center justify-center rounded-xl border border-[var(--border-subtle)] text-[var(--text-muted)] hover:text-[var(--state-danger)] hover:border-[var(--state-danger-border)] hover:bg-[var(--state-danger-bg)] transition-all duration-200 disabled:opacity-40"
                                title="Delete permanently"
                              >
                                <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                              </button>
                            </PermissionGate>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {paged.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-6 py-16 text-center text-[14px] text-[var(--text-muted)]">
                        No users found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-6 py-5 border-t border-[var(--border-subtle)] flex items-center justify-between">
                <p className="text-[13px] text-[var(--text-muted)]">
                  Showing {start + 1}–{Math.min(start + itemsPerPage, filteredUsers.length)} of {filteredUsers.length}
                </p>
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className={cn(
                      'w-8 h-8 flex items-center justify-center rounded-full border text-[14px] font-semibold transition-all duration-200',
                      currentPage === 1
                        ? 'border-[var(--border-subtle)] text-[var(--text-muted)] cursor-not-allowed opacity-50'
                        : 'border-[var(--border-subtle)] text-[var(--text-secondary)] hover:border-[var(--border-default)] hover:bg-[var(--bg-base)]'
                    )}
                  >
                    ‹
                  </button>
                  {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={cn(
                        'w-8 h-8 flex items-center justify-center rounded-full text-[14px] font-semibold transition-all duration-200',
                        currentPage === page
                          ? 'bg-[var(--brand-forest)] text-white'
                          : 'border border-[var(--border-subtle)] text-[var(--text-secondary)] hover:border-[var(--border-default)] hover:bg-[var(--bg-base)]'
                      )}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className={cn(
                      'w-8 h-8 flex items-center justify-center rounded-full border text-[14px] font-semibold transition-all duration-200',
                      currentPage === totalPages
                        ? 'border-[var(--border-subtle)] text-[var(--text-muted)] cursor-not-allowed opacity-50'
                        : 'border-[var(--border-subtle)] text-[var(--text-secondary)] hover:border-[var(--border-default)] hover:bg-[var(--bg-base)]'
                    )}
                  >
                    ›
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {selectedUser && (
        <UserDetailModal user={selectedUser} onClose={() => setSelectedUser(null)} />
      )}

      {devicesUser && (
        <UserDevicesModal user={devicesUser} onClose={() => setDevicesUser(null)} />
      )}

      {showCreateModal && (
        <CreateUserModal onClose={() => setShowCreateModal(false)} />
      )}

      {editUser && (
        <UserEditModal
          user={editUser}
          onClose={() => setEditUser(null)}
          onSaved={() => { setEditUser(null); queryClient.invalidateQueries({ queryKey: ['admin-users'] }); }}
        />
      )}
    </div>
    </PageGuard>
  );
};

/** Registered push devices for one user — app, platform, and installed version,
 *  so support can see who is on an old build before raising min_version. */
function UserDevicesModal({ user, onClose }: { user: UserSummaryResponse; onClose: () => void }) {
  const { data: devices, isLoading } = useQuery({
    queryKey: ['user-devices', user.userId],
    queryFn: () => opsService.devices(user.userId),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="w-full max-w-md rounded-2xl bg-[var(--bg-surface)] border border-[var(--border-subtle)] shadow-xl p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-[16px] font-semibold text-[var(--text-primary)]">
            Devices — {user.firstName} {user.lastName}
          </h3>
          <button onClick={onClose} className="text-[var(--text-muted)] hover:text-[var(--text-primary)]">
            <X className="w-4 h-4" />
          </button>
        </div>
        {isLoading ? (
          <p className="text-[13px] text-[var(--text-muted)] py-6 text-center">Loading…</p>
        ) : !devices || devices.length === 0 ? (
          <p className="text-[13px] text-[var(--text-muted)] py-6 text-center">
            No registered devices — this user has never signed into an app.
          </p>
        ) : (
          <div className="space-y-2">
            {devices.map((d, i) => (
              <div key={i} className="flex items-center justify-between rounded-xl border border-[var(--border-subtle)] px-4 py-3">
                <div>
                  <p className="text-[13px] font-medium text-[var(--text-primary)] capitalize">
                    {String(d.app)} · {String(d.platform)}
                  </p>
                  <p className="text-[12px] text-[var(--text-muted)]">
                    Last seen {d.last_seen_at ? new Date(String(d.last_seen_at)).toLocaleString() : 'never'}
                  </p>
                </div>
                <span className="text-[12px] font-semibold px-2.5 py-1 rounded-full bg-[var(--bg-base)] border border-[var(--border-subtle)] text-[var(--text-secondary)]">
                  {d.app_version ? `v${String(d.app_version)}` : 'version unknown'}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
